import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import useModalStore from '@/stores/modalStore'
import { useToast } from '@/hooks/use-toast'
import { createLead } from '@/services/leads'
import { Loader2 } from 'lucide-react'

const formSchema = z.object({
  full_name: z.string().min(2, {
    message: 'O nome deve ter pelo menos 2 caracteres.',
  }),
  email: z.string().email({
    message: 'Por favor insira um email válido.',
  }),
  business_area: z.string({
    required_error: 'Por favor selecione uma área de negócio.',
  }),
  message: z.string().optional(),
})

const businessAreas = [
  'Logística',
  'Retalho',
  'Serviços',
  'Indústria',
  'Imobiliário',
  'Recursos Humanos',
]

export function DemoModal() {
  const { isOpen, closeModal } = useModalStore()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: '',
      email: '',
      business_area: undefined,
      message: '',
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true)
    try {
      const { error } = await createLead({
        full_name: values.full_name,
        email: values.email,
        business_area: values.business_area,
        message: values.message,
      })

      if (error) throw error

      toast({
        title: 'Pedido recebido!',
        description:
          'Obrigado! O seu pedido de demonstração foi enviado com sucesso.',
      })
      form.reset()
      closeModal()
    } catch (error) {
      console.error('Error submitting lead:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar',
        description:
          'Ocorreu um erro ao enviar o pedido. Por favor, tente novamente.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Pedir demonstração</DialogTitle>
          <DialogDescription>
            Preencha o formulário abaixo para agendar uma demonstração das
            nossas soluções.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Completo</FormLabel>
                  <FormControl>
                    <Input placeholder="Seu nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email de Negócio</FormLabel>
                  <FormControl>
                    <Input placeholder="seu@empresa.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="business_area"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Área de Negócio</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a sua área" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {businessAreas.map((area) => (
                        <SelectItem key={area} value={area}>
                          {area}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mensagem (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Conte-nos um pouco sobre o seu negócio e desafios..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Pedido'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
